import type React from "react";
import { Group, Loader, Text, Tooltip } from "@mantine/core";
import { Button } from "@xplatform/design-system";
import { IconAlertTriangle, IconCloudCheck } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import type { RowSaveStatus } from "@hooks/useJobResults";

interface RowSaveIndicatorProps {
  saveState: RowSaveStatus | undefined;
  onRetrySave?: () => void;
}

const RowSaveIndicator: React.FC<RowSaveIndicatorProps> = ({
  saveState,
  onRetrySave,
}) => {
  const { t } = useTranslation();
  if (!saveState) return null;

  if (saveState.state === "saving") {
    return (
      <Group gap={4} align="center">
        <Loader size={12} />
        <Text size="xs" c="dimmed">
          {t("resultRow.save.saving")}
        </Text>
      </Group>
    );
  }

  if (saveState.state === "saved") {
    return (
      <Group gap={4} align="center">
        <IconCloudCheck size={14} color="var(--mantine-color-teal-6)" />
        <Text size="xs" c="teal">
          {t("resultRow.save.saved")}
        </Text>
      </Group>
    );
  }

  if (saveState.state === "failed") {
    return (
      <Group gap={4} align="center" wrap="nowrap">
        <Tooltip
          label={saveState.error ?? t("resultRow.save.failed")}
          multiline
        >
          <Group gap={4} align="center">
            <IconAlertTriangle size={14} color="var(--mantine-color-red-6)" />
            <Text size="xs" c="red">
              {t("resultRow.save.failed")}
            </Text>
          </Group>
        </Tooltip>
        {onRetrySave && (
          <Button
            size="compact-xs"
            variant="subtle"
            color="red"
            onClick={onRetrySave}
          >
            {t("resultRow.save.retry")}
          </Button>
        )}
      </Group>
    );
  }

  return null;
};

export default RowSaveIndicator;
